<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
} from "../composables/useSeo";
import { usePhoneNumber } from "../composables/usePhoneNumber";

const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();

useSeoBase({
  title: "FAQ | Frequently Asked Questions | Junk Removal | Junk Guys MN",
  description:
    "Get answers to frequently asked questions about Junk Guys MN junk removal services, pricing, and process.",
  canonical: "https://junkguysmn.com/faq",
});

useJsonLd([orgNode(), localBusinessNode()]);

// FAQ data organized by categories
const faqCategories = [
  {
    title: "General Questions",
    icon: "❓",
    faqs: [
      {
        q: "What areas do you serve?",
        a: "We serve the greater Minnesota area including Minneapolis, Saint Paul, Bloomington, Eden Prairie, Edina, Richfield, Maple Grove, Plymouth, Woodbury, and many surrounding communities.",
      },
      {
        q: "Do you offer same-day service?",
        a: "Yes, we often have same-day availability! It's best to call early in the day to secure a spot. Availability depends on our current schedule and your location.",
      },
      {
        q: "What items can you remove?",
        a: "We can remove most household and commercial items including furniture, appliances, electronics, construction debris, yard waste, and general junk. We cannot remove hazardous materials, chemicals, or medical waste.",
      },
    ],
  },
  {
    title: "Pricing & Estimates",
    icon: "💰",
    faqs: [
      {
        q: "How do you determine pricing?",
        a: "Our pricing is volume-based, meaning we charge based on how much space your items take up in our truck. We provide on-site pricing confirmation to ensure accuracy.",
      },
      {
        q: "Can I get an estimate before you arrive?",
        a: "Yes! You can text us photos of your items for a rough estimate. For the most accurate pricing, we'll confirm the final amount on-site.",
      },
      {
        q: "Are there any hidden fees?",
        a: "No hidden fees! Our quoted price includes pickup, loading, transportation, and disposal. What we quote is what you pay.",
      },
    ],
  },
  {
    title: "Service Process",
    icon: "⚙️",
    faqs: [
      {
        q: "How long does a typical job take?",
        a: "Most jobs take 1-3 hours depending on the amount of items and complexity. We'll give you a time estimate when we arrive.",
      },
      {
        q: "Do I need to be present during the service?",
        a: "While you don't need to be present, we do need someone to confirm the final price and sign off on the work. We can arrange for a neighbor or property manager if needed.",
      },
      {
        q: "Do you clean up after removal?",
        a: "Yes! We sweep and clean the area after removing items, leaving your space clean and ready to use.",
      },
    ],
  },
  {
    title: "Disposal & Recycling",
    icon: "♻️",
    faqs: [
      {
        q: "What happens to my items after removal?",
        a: "We're committed to responsible disposal. Usable items are donated to local charities, recyclable materials are sent to recycling facilities, and only true waste goes to the landfill.",
      },
      {
        q: "Can I get disposal receipts?",
        a: "Yes, disposal receipts are available upon request. This can be helpful for tax purposes or business records.",
      },
    ],
  },
];
</script>

<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Hero Section -->
    <section
      class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white"
    >
      <div class="mx-auto max-w-7xl px-4 py-16 lg:py-24">
        <div class="text-center max-w-3xl mx-auto">
          <h1 class="text-4xl lg:text-6xl font-bold mb-6">
            Frequently Asked Questions
          </h1>
          <p class="text-xl lg:text-2xl text-slate-200 leading-relaxed">
            Get answers to common questions about our junk removal services,
            pricing, and process.
          </p>
          <div class="mt-8 flex flex-wrap justify-center gap-4">
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="inline-flex items-center justify-center px-8 py-4 bg-primary-main hover:bg-primary-dark text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              📞 Call {{ phoneNumberPretty }}
            </a>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-all duration-200"
            >
              📅 Book Online
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Categories -->
    <section class="py-16">
      <div class="mx-auto max-w-6xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Find Your Answer
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Browse our FAQ categories below to quickly find the information you
            need.
          </p>
        </div>

        <!-- FAQ Grid -->
        <div class="space-y-8">
          <div
            v-for="category in faqCategories"
            :key="category.title"
            class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
          >
            <!-- Category Header -->
            <div
              class="bg-gradient-to-r from-primary-main/5 to-primary-light/10 p-6 border-b border-slate-200"
            >
              <div class="flex items-center gap-4">
                <div class="text-3xl">{{ category.icon }}</div>
                <h3 class="text-2xl font-bold text-slate-900">
                  {{ category.title }}
                </h3>
              </div>
            </div>

            <!-- FAQ Items -->
            <div class="divide-y divide-slate-200">
              <details
                v-for="(faq, index) in category.faqs"
                :key="index"
                class="group"
              >
                <summary
                  class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
                >
                  <div class="flex items-center justify-between">
                    <span
                      class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                    >
                      {{ faq.q }}
                    </span>
                    <div
                      class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                    >
                      <svg
                        class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </summary>
                <div class="px-6 pb-6">
                  <div
                    class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
                  >
                    {{ faq.a }}
                  </div>
                </div>
              </details>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Quick Contact Section -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-4xl px-4 text-center">
        <h2 class="text-3xl font-bold text-slate-900 mb-6">
          Still Have Questions?
        </h2>
        <p class="text-lg text-slate-600 mb-8">
          Can't find what you're looking for? Our team is here to help with
          personalized assistance.
        </p>

        <div class="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          <div class="bg-primary-main/5 rounded-xl p-6">
            <div class="text-3xl mb-3">📞</div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Call Us Directly
            </h3>
            <p class="text-slate-600 mb-4">
              Speak with our team for immediate answers
            </p>
            <a
              :href="phoneNumberTel"
              class="inline-flex items-center justify-center w-full bg-primary-main hover:bg-primary-dark text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Call {{ phoneNumberPretty }}
            </a>
          </div>

          <div class="bg-primary-light/10 rounded-xl p-6">
            <div class="text-3xl mb-3">📅</div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Book Online
            </h3>
            <p class="text-slate-600 mb-4">
              Schedule your service in just a few clicks
            </p>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center w-full bg-primary-light hover:bg-primary-dark text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Book Service
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="bg-primary-main text-white py-16">
      <div class="mx-auto max-w-4xl px-4 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold mb-4">
          Ready to Get Started?
        </h2>
        <p class="text-xl text-white mb-8">
          Don't let your questions hold you back. Get your junk removal done
          today!
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <a
            :href="phoneNumberTel"
            class="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-main font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            📞 Call Now
          </a>
          <NuxtLink
            to="/book"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-main transition-all duration-200"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
